import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useClient } from 'jsonapi-react';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { cn } from '@/lib/utils';
import { LiveClassCardTeacher } from './LiveClassCardTeacher';
import { getLiveClassStatus } from './live-class-status';

const WEEKDAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Semana domingo-sábado, calculada a partir do dia da semana (não da locale do
// moment), pra não depender de configuração de locale nos testes/telas que
// renderizam isso.
function startOfWeek(date) {
  return date.clone().day(0).startOf('day');
}

export function LiveClassesCalendarTeacher({ liveClasses, setWeekDays, onSelectLiveClass, onEditLiveClass, onDeleteLiveClass }) {
  const client = useClient();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(moment()));
  const [loading, setLoading] = useState(true);
  const weekEnd = weekStart.clone().add(7, 'days');

  useEffect(() => {
    let isCurrent = true;

    const fetchLiveClasses = async () => {
      setLoading(true);
      try {
        const initialDate = weekStart.format('YYYY-MM-DD');
        const finalDate = weekEnd.clone().subtract(1, 'day').format('YYYY-MM-DD');
        const response = await client.fetch(`/live_classrooms?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${finalDate}`);

        if (isCurrent) setWeekDays(response.data || []);
      } catch (error) {
        console.error(error);
      }
      if (isCurrent) setLoading(false);
    };

    fetchLiveClasses();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const liveClassesInWeek = liveClasses.filter(liveClass => moment(liveClass['initial-date']).isBetween(weekStart, weekEnd, null, '[)'));

  return (
    <div
      className="rounded-[24px] border border-neutral-lightest p-5"
      style={{ background: 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 44%, #FFFCF1 87.5%)' }}
    >
      <div className="relative mb-5 flex items-center justify-between border-b border-white/60 pb-4">
        <button
          type="button"
          aria-label="Semana anterior"
          onClick={() => setWeekStart(current => current.clone().subtract(7, 'days'))}
          className="flex items-center gap-2 text-xs font-semibold text-neutral-dark"
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: 'var(--gradient-btn-continue)' }}
          >
            <FiArrowLeft className="h-4 w-4" />
          </span>
          <span className="hidden sm:inline">Semana anterior</span>
        </button>

        <h2 className="absolute left-1/2 mb-0 -translate-x-1/2 text-center text-sm font-extrabold text-neutral-900">
          Semana de {weekStart.format('DD/MM')} - {weekEnd.clone().subtract(1, 'day').format('DD/MM')}
        </h2>

        <button
          type="button"
          aria-label="Próxima semana"
          onClick={() => setWeekStart(current => current.clone().add(7, 'days'))}
          className="flex items-center gap-2 text-xs font-semibold text-neutral-dark"
        >
          <span className="hidden sm:inline">Próxima semana</span>
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: 'var(--gradient-btn-continue)' }}
          >
            <FiArrowRight className="h-4 w-4" />
          </span>
        </button>
      </div>

      {loading && <Loader />}

      {!loading && liveClassesInWeek.length === 0 && (
        <EmptyState
          type="data"
          title="Sem aulas ao vivo nesta semana"
          variant="plain"
          className="pt-32"
        />
      )}

      {!loading && liveClassesInWeek.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-7">
          {WEEKDAY_LABELS.map((weekDayLabel, dayOffset) => {
            const day = weekStart.clone().add(dayOffset, 'days');
            const isToday = day.isSame(moment(), 'day');
            const dayLiveClasses = liveClassesInWeek.filter(liveClass => moment(liveClass['initial-date']).isSame(day, 'day'));

            return (
              <div
                key={weekDayLabel}
                className={cn('rounded-2xl p-3', isToday && 'bg-white/60')}
              >
                <div className={cn('mb-4 flex items-baseline gap-1.5 text-sm font-bold', isToday ? 'text-primary-dark' : 'text-neutral-dark')}>
                  <span>{day.format('DD')}</span>
                  <span>{weekDayLabel}</span>
                </div>

                <div className="flex flex-col gap-3">
                  {dayLiveClasses.map(liveClass => (
                    <LiveClassCardTeacher
                      key={liveClass.id}
                      liveClass={liveClass}
                      status={getLiveClassStatus(liveClass)}
                      onClick={() => onSelectLiveClass(liveClass)}
                      onEdit={onEditLiveClass}
                      onDelete={onDeleteLiveClass}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
